import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  alpha,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Business,
  Person,
  ArrowBack,
  ArrowForward,
  Star,
  Rocket,
  Add,
  Remove,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SERVICE_TIERS, formatPrice, getTier } from '../lib/serviceTiers';
import { SERVICE_ADD_ONS, ServiceAddOn, getAddOnsForTier, formatAddOnPrice } from '../lib/serviceAddOns';
import { useRecaptcha } from '../hooks/useRecaptcha';

const steps = ['Choose Plan', 'Company Info', 'Account Details', 'Add-ons & Review'];

interface FormData {
  // Plan selection
  selectedTier: string;
  selectedAddOns: string[];
  
  // Company info
  companyName: string;
  companyWebsite: string;
  industry: string;
  employeeCount: string;
  
  // Account details
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  
  // Terms
  acceptTerms: boolean;
  acceptMarketing: boolean;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTier = searchParams.get('tier') || 'professional';
  const { executeRecaptcha } = useRecaptcha();
  
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  const [formData, setFormData] = useState<FormData>({
    selectedTier: preselectedTier,
    selectedAddOns: [],
    companyName: '',
    companyWebsite: '',
    industry: '',
    employeeCount: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    acceptMarketing: false,
  });

  const toggleAddOn = (addOnId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedAddOns: prev.selectedAddOns.includes(addOnId)
        ? prev.selectedAddOns.filter(id => id !== addOnId)
        : [...prev.selectedAddOns, addOnId],
    }));
  };

  const handleInputChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));
    setError(null);
  };

  const selectTier = (tierId: string) => {
    setFormData(prev => ({ ...prev, selectedTier: tierId }));
  };

  const validateStep = (): boolean => {
    switch (activeStep) {
      case 0:
        return !!formData.selectedTier;
      case 1:
        return !!(formData.companyName && formData.industry);
      case 2:
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
          setError('Please fill in all required fields');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        if (formData.password.length < 8) {
          setError('Password must be at least 8 characters');
          return false;
        }
        return true;
      case 3:
        if (!formData.acceptTerms) {
          setError('You must accept the terms of service');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep(prev => prev + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Execute reCAPTCHA verification
      const recaptchaToken = await executeRecaptcha('REGISTER');
      if (!recaptchaToken) {
        throw new Error('reCAPTCHA verification failed. Please try again.');
      }
      
      const tier = getTier(formData.selectedTier);
      if (!tier) throw new Error('Invalid tier selected');
      
      // Create customer account via API
      const response = await fetch('/api/customers/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Account
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          // Company
          companyName: formData.companyName,
          website: formData.companyWebsite || '',
          industry: formData.industry,
          companySize: formData.employeeCount,
          // Tier
          tierId: formData.selectedTier,
          billingCycle: 'monthly',
          // Add-ons
          addOns: formData.selectedAddOns,
          // Legal
          agreedToTerms: formData.acceptTerms,
          // reCAPTCHA
          recaptchaToken,
        }),
      });
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Registration failed');
      }

      // Account created — show success screen, then redirect to login
      setRegisteredEmail(formData.email);
      setRegistrationComplete(true);
      setTimeout(() => navigate('/login?registered=true'), 6000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedTier = getTier(formData.selectedTier);

  // Step 1: Plan Selection
  const renderPlanSelection = () => (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom textAlign="center">
        Choose Your Package
      </Typography>
      <Typography color="text.secondary" textAlign="center" mb={4}>
        Enterprise software consultancy packages tailored to your business needs
      </Typography>
      
      <Grid container spacing={3}>
        {SERVICE_TIERS.map((tier) => (
          <Grid item xs={12} md={6} lg={3} key={tier.id}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                position: 'relative',
                border: formData.selectedTier === tier.id 
                  ? '2px solid' 
                  : '1px solid',
                borderColor: formData.selectedTier === tier.id 
                  ? 'primary.main' 
                  : 'divider',
                transition: 'all 0.2s ease',
                mt: tier.recommended ? 2 : 0,
                overflow: 'visible',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => selectTier(tier.id)}
            >
              {tier.recommended && (
                <Chip
                  label="RECOMMENDED"
                  color="primary"
                  size="small"
                  icon={<Star sx={{ fontSize: 14 }} />}
                  sx={{
                    position: 'absolute',
                    top: -14,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 28,
                    zIndex: 1,
                    boxShadow: 2,
                  }}
                />
              )}
              
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {tier.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2} sx={{ minHeight: 40 }}>
                  {tier.tagline}
                </Typography>
                
                {/* Monthly Maintenance Fee - First Payment */}
                <Box mb={1} sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    MONTHLY MAINTENANCE
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="primary">
                    {tier.monthlyFee > 0 ? formatPrice(tier.monthlyFee) : 'Custom'}/mo
                  </Typography>
                </Box>
                
                {/* Retainer Fee - Second Payment */}
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    PACKAGE RETAINER
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {formatPrice(tier.retainerFee)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tier.enterprise ? 'per quarter' : 'one-time'}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                {tier.includedHours && (
                  <Typography variant="body2" color="primary" fontWeight={600} mb={1}>
                    {tier.includedHours} included
                  </Typography>
                )}
                
                <Stack spacing={1}>
                  {tier.highlights.map((highlight, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="body2">{highlight}</Typography>
                    </Box>
                  ))}
                </Stack>
                
                {formData.selectedTier === tier.id && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                    }}
                  >
                    <CheckCircle color="primary" />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Payment Flow Explanation */}
      <Paper variant="outlined" sx={{ p: 3, mt: 4, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          How Payments Work
        </Typography>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Chip label="1" size="small" color="primary" sx={{ fontWeight: 700, minWidth: 28 }} />
            <Typography variant="body2">
              <strong>Monthly Maintenance Subscription</strong> — Start with your monthly maintenance fee to activate your account and begin onboarding.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Chip label="2" size="small" color="primary" sx={{ fontWeight: 700, minWidth: 28 }} />
            <Typography variant="body2">
              <strong>Package Retainer</strong> — Once subscription is verified, pay the package retainer to begin development work.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );

  // Step 2: Company Info
  const renderCompanyInfo = () => (
    <Box maxWidth={600} mx="auto">
      <Typography variant="h5" fontWeight={700} gutterBottom textAlign="center">
        Tell Us About Your Company
      </Typography>
      <Typography color="text.secondary" textAlign="center" mb={4}>
        Help us customize your experience
      </Typography>
      
      <Stack spacing={3}>
        <TextField
          label="Company Name"
          value={formData.companyName}
          onChange={handleInputChange('companyName')}
          fullWidth
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Business />
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          label="Company Website"
          value={formData.companyWebsite}
          onChange={handleInputChange('companyWebsite')}
          fullWidth
          placeholder="https://example.com"
        />
        
        <TextField
          label="Industry"
          value={formData.industry}
          onChange={handleInputChange('industry')}
          fullWidth
          required
          placeholder="e.g., Technology, Healthcare, Finance"
        />
        
        <TextField
          label="Number of Employees"
          value={formData.employeeCount}
          onChange={handleInputChange('employeeCount')}
          fullWidth
          placeholder="e.g., 1-10, 11-50, 51-200"
        />
      </Stack>
    </Box>
  );

  // Step 3: Account Details
  const renderAccountDetails = () => (
    <Box maxWidth={600} mx="auto">
      <Typography variant="h5" fontWeight={700} gutterBottom textAlign="center">
        Create Your Account
      </Typography>
      <Typography color="text.secondary" textAlign="center" mb={4}>
        Set up your login credentials
      </Typography>
      
      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={handleInputChange('firstName')}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={handleInputChange('lastName')}
              fullWidth
              required
            />
          </Grid>
        </Grid>
        
        <TextField
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleInputChange('email')}
          fullWidth
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person />
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          label="Phone Number"
          value={formData.phone}
          onChange={handleInputChange('phone')}
          fullWidth
          placeholder="+1 (555) 000-0000"
        />
        
        <TextField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleInputChange('password')}
          fullWidth
          required
          helperText="At least 8 characters"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          label="Confirm Password"
          type={showPassword ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={handleInputChange('confirmPassword')}
          fullWidth
          required
        />
      </Stack>
    </Box>
  );

  // Step 4: Add-ons & Review
  const renderReview = () => {
    const availableAddOns = getAddOnsForTier(formData.selectedTier);
    const selectedAddOnDetails = formData.selectedAddOns
      .map(id => SERVICE_ADD_ONS.find(a => a.id === id))
      .filter((a): a is ServiceAddOn => !!a);
    
    // Calculate totals
    const addOnsRecurringTotal = selectedAddOnDetails
      .filter(a => a.type === 'recurring')
      .reduce((sum, a) => sum + a.price, 0);
    const addOnsOneTimeTotal = selectedAddOnDetails
      .filter(a => a.type === 'one_time')
      .reduce((sum, a) => sum + a.price, 0);
    
    return (
      <Box maxWidth={800} mx="auto">
        <Typography variant="h5" fontWeight={700} gutterBottom textAlign="center">
          Customize & Review Your Order
        </Typography>
        <Typography color="text.secondary" textAlign="center" mb={4}>
          Enhance your plan with powerful add-ons and review your order
        </Typography>
        
        {/* Add-ons Section */}
        {availableAddOns.length > 0 && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              <Rocket sx={{ mr: 1, verticalAlign: 'middle' }} />
              Boost Your Results with Add-ons
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Select optional add-ons to supercharge your lead generation
            </Typography>
            
            <Grid container spacing={2}>
              {availableAddOns.map((addOn) => {
                const isSelected = formData.selectedAddOns.includes(addOn.id);
                return (
                  <Grid item xs={12} sm={6} key={addOn.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: isSelected ? alpha('#3366FF', 0.05) : 'background.paper',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: alpha('#3366FF', 0.05),
                        },
                      }}
                      onClick={() => toggleAddOn(addOn.id)}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Box flex={1}>
                            <Typography fontWeight={600}>
                              {addOn.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {addOn.description}
                            </Typography>
                            <Typography fontWeight={700} color="primary" mt={1}>
                              {formatAddOnPrice(addOn)}
                            </Typography>
                          </Box>
                          <IconButton
                            color={isSelected ? 'error' : 'primary'}
                            sx={{
                              bgcolor: isSelected ? alpha('#FF4444', 0.1) : alpha('#3366FF', 0.1),
                              ml: 2,
                            }}
                          >
                            {isSelected ? <Remove /> : <Add />}
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}
        
        {/* Order Summary - Two Payment Structure */}
        {selectedTier && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Payment Summary
            </Typography>
            
            {/* Payment 1: Monthly Maintenance */}
            <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 2, borderRadius: 1, mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="overline" sx={{ opacity: 0.9 }}>
                    PAYMENT 1 — DUE TODAY
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    Monthly Maintenance Subscription
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={800}>
                  {selectedTier.monthlyFee > 0 ? formatPrice(selectedTier.monthlyFee) : 'Custom'}
                </Typography>
              </Stack>
              {addOnsRecurringTotal > 0 && (
                <Stack spacing={0.5} mt={1} sx={{ opacity: 0.9 }}>
                  {selectedAddOnDetails.filter(a => a.type === 'recurring').map((addOn) => (
                    <Stack key={addOn.id} direction="row" justifyContent="space-between">
                      <Typography variant="body2">+ {addOn.name}</Typography>
                      <Typography variant="body2">{formatAddOnPrice(addOn)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
            
            {/* Payment 2: Retainer */}
            <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    PAYMENT 2 — AFTER SUBSCRIPTION VERIFIED
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {selectedTier.name} Package Retainer
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {formatPrice(selectedTier.retainerFee)}
                </Typography>
              </Stack>
              {addOnsOneTimeTotal > 0 && (
                <Stack spacing={0.5} mt={1}>
                  {selectedAddOnDetails.filter(a => a.type === 'one_time').map((addOn) => (
                    <Stack key={addOn.id} direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">+ {addOn.name}</Typography>
                      <Typography variant="body2">{formatAddOnPrice(addOn)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Today's Payment
                </Typography>
                <Typography variant="h5" fontWeight={800} color="primary">
                  {formatPrice(selectedTier.monthlyFee + addOnsRecurringTotal)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Monthly subscription
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Project Investment
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatPrice(
                    selectedTier.retainerFee + 
                    selectedTier.monthlyFee + 
                    addOnsRecurringTotal + 
                    addOnsOneTimeTotal
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  First month + retainer + add-ons
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}
        
        {/* Account Summary */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Company
              </Typography>
              <Typography fontWeight={600}>{formData.companyName}</Typography>
              {formData.companyWebsite && (
                <Typography variant="body2" color="text.secondary">
                  {formData.companyWebsite}
                </Typography>
              )}
              <Typography variant="body2">{formData.industry}</Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Account
              </Typography>
              <Typography fontWeight={600}>
                {formData.firstName} {formData.lastName}
              </Typography>
              <Typography variant="body2">{formData.email}</Typography>
              {formData.phone && (
                <Typography variant="body2">{formData.phone}</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
        
        <Box mt={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.acceptTerms}
                onChange={handleInputChange('acceptTerms')}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <Link href="/terms" target="_blank">Terms of Service</Link>
                {' '}and{' '}
                <Link href="/privacy" target="_blank">Privacy Policy</Link>
              </Typography>
            }
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.acceptMarketing}
                onChange={handleInputChange('acceptMarketing')}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                Send me product updates and marketing communications
              </Typography>
            }
          />
        </Box>
      </Box>
    );
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderPlanSelection();
      case 1:
        return renderCompanyInfo();
      case 2:
        return renderAccountDetails();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A0E27 0%, #1a1f3c 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            REDEX
          </Typography>
          <Typography color="grey.400">
            Enterprise software consultancy tailored to your business needs
          </Typography>
        </Box>
        
        {/* Main Content */}
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            background: alpha('#fff', 0.02),
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: alpha('#fff', 0.1),
          }}
        >
          {registrationComplete ? (
            <Box textAlign="center" py={6}>
              <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 3 }} />
              <Typography variant="h4" fontWeight={700} color="white" gutterBottom>
                Account Created!
              </Typography>
              <Typography variant="body1" color="grey.400" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
                Payment request links have been sent to <strong style={{ color: 'white' }}>{registeredEmail}</strong>.
                Complete your retainer payment to activate your account and access the REDEX dashboard.
              </Typography>
              <Alert severity="info" sx={{ maxWidth: 480, mx: 'auto', mb: 3, textAlign: 'left' }}>
                Check your inbox — you'll receive separate links for your setup retainer and monthly subscription. Payments can be made via ACH bank transfer or USDC.
              </Alert>
              <Typography variant="caption" color="grey.600">
                Redirecting to login in a few seconds…
              </Typography>
            </Box>
          ) : (
          <>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      color: 'grey.500',
                      '&.Mui-active': { color: 'primary.main', fontWeight: 600 },
                      '&.Mui-completed': { color: 'success.main' },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {/* Step Content */}
          <Box sx={{ minHeight: 400 }}>
            {getStepContent()}
          </Box>
          
          {/* Navigation Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 4,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={activeStep === 0 ? () => window.location.href = '/pricing.html' : handleBack}
              disabled={loading}
            >
              {activeStep === 0 ? 'Back to Pricing' : 'Back'}
            </Button>
            
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleNext}
                size="large"
                sx={{
                  px: 4,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !formData.acceptTerms}
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <Rocket />}
                sx={{
                  px: 4,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                {loading ? 'Processing...' : 'Complete Registration'}
              </Button>
            )}
          </Box>
          </>
          )}
        </Paper>
        
        {/* Login Link */}
        <Box textAlign="center" mt={3}>
          <Typography color="grey.500">
            Already have an account?{' '}
            <Link
              href="/login"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default RegisterPage;
